<template>
    <div v-if="config != null && config.cookieEnable">
		<div v-if="showBanner" class="cookies-warning fixed bottom-0 inset-x-0 z-[1000] border-t border-slate-200/80 bg-white text-slate-900 shadow-base-5 dark:border-slate-700/70 dark:bg-slate-900 dark:text-slate-100">
			<div class="cookie-banner-shell mx-auto max-w-[1320px] px-base-6 pt-base-5 pb-base-5 md:px-base-8 md:pt-base-5 md:pb-base-5">
				<div class="cookie-banner-content mx-auto max-w-[980px] flex flex-col items-center gap-base-4 md:gap-base-6">
					<div class="cookie-banner-text-wrap w-full">
						<p class="cookie-banner-text text-sm md:text-base leading-relaxed">
							{{ uiText.mainTextStart }}
							<a :href="termsUrl" target="_blank" rel="noopener" class="font-semibold text-sky-700 hover:text-sky-800 dark:text-sky-300 dark:hover:text-sky-200">{{ uiText.termsInText }}</a>{{ uiText.mainTextMiddleOne }}
							<a :href="privacyUrl" target="_blank" rel="noopener" class="font-semibold text-sky-700 hover:text-sky-800 dark:text-sky-300 dark:hover:text-sky-200">{{ uiText.privacyInText }}</a>{{ uiText.mainTextMiddleTwo }}
							<a :href="cookiesUrl" target="_blank" rel="noopener" class="font-semibold text-sky-700 hover:text-sky-800 dark:text-sky-300 dark:hover:text-sky-200">{{ uiText.cookiesInText }}</a>{{ uiText.mainTextEnd }}
						</p>
					</div>
					<div class="cookie-banner-actions flex shrink-0 flex-wrap items-center justify-center gap-base-2">
						<BaseButton class="cookie-btn-accept" :size="isMobile ? 'sm' : 'sm'" type="success" @click="acceptAll">{{ uiText.acceptAll }}</BaseButton>
						<BaseButton class="cookie-btn-reject" :size="isMobile ? 'sm' : 'sm'" type="secondary-outlined" @click="rejectAll">{{ uiText.rejectAll }}</BaseButton>
						<BaseButton class="cookie-btn-customize" :size="isMobile ? 'sm' : 'sm'" @click="openPreferences">{{ uiText.customize }}</BaseButton>
					</div>
				</div>
			</div>
		</div>
		<div v-if="showPreferences" class="fixed inset-0 z-[1100] flex items-end justify-center p-base-4 md:items-center md:p-base-6">
			<div class="absolute inset-0 bg-slate-900/35" @click="closePreferences"></div>
			<div class="cookie-preferences-panel relative w-full max-w-[860px] max-h-[88vh] overflow-y-auto rounded-base-4 border border-slate-200/80 bg-white p-base-5 md:p-base-9 shadow-base-5 dark:border-slate-700/70 dark:bg-slate-900 dark:text-slate-100">
				<h3 class="text-base-lg font-extrabold mb-base-2 md:mb-base-3">{{ uiText.preferencesTitle }}</h3>
				<p class="cookie-modal-copy text-sm md:text-base leading-relaxed mb-base-4 md:mb-base-6">{{ uiText.preferencesDescription }}</p>
				<div class="flex flex-col gap-3 md:gap-base-4 pb-2">
					<div class="rounded-base-3 border border-slate-200 bg-white/80 p-base-4 md:p-base-5 dark:border-slate-700 dark:bg-slate-800/50">
						<div class="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-x-base-3 gap-y-base-2">
							<h4 class="font-bold leading-snug">{{ uiText.strictlyNecessaryTitle }}</h4>
							<span class="text-xs font-semibold px-base-2 py-1 rounded-full bg-slate-100 dark:bg-slate-800">{{ uiText.alwaysActive }}</span>
							<p class="cookie-modal-copy col-span-2 text-sm leading-relaxed pe-base-2">{{ uiText.strictlyNecessaryDescription }}</p>
						</div>
					</div>
					<div class="rounded-base-3 border border-slate-200 bg-white/80 p-base-4 md:p-base-5 dark:border-slate-700 dark:bg-slate-800/50">
						<div class="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-x-base-3 gap-y-base-2">
							<h4 class="font-bold leading-snug">{{ uiText.analyticsTitle }}</h4>
							<label class="inline-flex items-center cursor-pointer">
								<input type="checkbox" class="sr-only peer" v-model="draft.analytics">
								<span class="relative w-11 h-6 bg-slate-300 peer-focus:outline-none rounded-full peer dark:bg-slate-700 peer-checked:bg-blue-600 after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-full"></span>
							</label>
							<p class="cookie-modal-copy col-span-2 text-sm leading-relaxed pe-base-2">{{ uiText.analyticsDescription }}</p>
						</div>
					</div>
					<div class="rounded-base-3 border border-slate-200 bg-white/80 p-base-4 md:p-base-5 dark:border-slate-700 dark:bg-slate-800/50">
						<div class="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-x-base-3 gap-y-base-2">
							<h4 class="font-bold leading-snug">{{ uiText.marketingTitle }}</h4>
							<label class="inline-flex items-center cursor-pointer">
								<input type="checkbox" class="sr-only peer" v-model="draft.marketing">
								<span class="relative w-11 h-6 bg-slate-300 peer-focus:outline-none rounded-full peer dark:bg-slate-700 peer-checked:bg-blue-600 after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-full"></span>
							</label>
							<p class="cookie-modal-copy col-span-2 text-sm leading-relaxed pe-base-2">{{ uiText.marketingDescription }}</p>
						</div>
					</div>
				</div>
				<div class="cookie-modal-actions mt-4 md:mt-5 flex flex-wrap items-center justify-center gap-base-2 md:gap-base-3">
					<BaseButton :size="isMobile ? 'sm' : 'md'" type="success" @click="acceptAll">{{ uiText.acceptAll }}</BaseButton>
					<BaseButton :size="isMobile ? 'sm' : 'md'" type="secondary-outlined" @click="rejectAll">{{ uiText.rejectAll }}</BaseButton>
					<BaseButton :size="isMobile ? 'sm' : 'md'" @click="savePreferences">{{ uiText.savePreferences }}</BaseButton>
				</div>
		</div>
		</div>
	</div>
</template>


<script>
import { mapState } from 'pinia'
import { useAppStore } from '@/store/app'
import localData from '@/utility/localData';
import BaseButton from '@/components/inputs/BaseButton.vue'

export default {
    components: { BaseButton },
    data(){
        return {
			storageKey: 'investments_cookie_preferences',
            showBanner: false,
			showPreferences: false,
			isMobile: false,
			lockScrollY: 0,
			termsUrl: 'https://investments.ro/sp/terms-of-service',
			privacyUrl: 'https://investments.ro/sp/privacy-policy',
			cookiesUrl: 'https://investments.ro/sp/cookies',
			draft: {
				analytics: false,
				marketing: false
			}
        }
    },
    computed: {
        ...mapState(useAppStore, ['config']),
		uiText() {
			const locale = (localData.get('locale', window.siteConfig.languageDefault) || '').toLowerCase()
			if (locale.startsWith('ro')) {
				return {
					mainTextStart: 'Folosim cookie-uri strict necesare pentru funcționarea site-ului și, doar cu acordul tău, cookie-uri de analiză și marketing. Detalii despre utilizarea platformei găsești în ',
					termsInText: 'regulile serviciului',
					mainTextMiddleOne: ', despre modul în care prelucrăm datele personale în ',
					privacyInText: 'informațiile de confidențialitate',
					mainTextMiddleTwo: ', iar despre utilizarea cookie-urilor și opțiunile tale în ',
					cookiesInText: 'explicațiile dedicate',
					mainTextEnd: '.',
					acceptAll: 'Acceptă toate',
					rejectAll: 'Respinge toate',
					customize: 'Personalizează',
					preferencesTitle: 'Preferințe cookie-uri',
					preferencesDescription: 'Puteți alege ce categorii de cookie-uri opționale permiteți. Cookie-urile strict necesare sunt utilizate întotdeauna deoarece sunt esențiale pentru funcționarea site-ului, autentificare și securitate.',
					strictlyNecessaryTitle: 'Strict necesare',
					alwaysActive: 'Activ mereu',
					strictlyNecessaryDescription: 'Necesare pentru funcționarea de bază a site-ului, autentificare, menținerea sesiunii, securitate și prevenirea abuzurilor. Aceste cookie-uri nu pot fi dezactivate din acest panou.',
					analyticsTitle: 'Analiză',
					analyticsDescription: 'Ne ajută să înțelegem modul în care este utilizat site-ul, să măsurăm traficul și să îmbunătățim performanța și experiența utilizatorilor.',
					marketingTitle: 'Marketing',
					marketingDescription: 'Utilizate pentru măsurarea performanței campaniilor, afișarea comunicărilor relevante și integrarea cu servicii promoționale sau publicitare, dacă acestea există.',
					savePreferences: 'Salvează preferințele'
				}
			}
			return {
				mainTextStart: 'We use strictly necessary cookies for core platform functionality and, only with your consent, analytics and marketing cookies. Details about platform usage are available in ',
				termsInText: 'service rules',
				mainTextMiddleOne: ', details about personal data processing are available in ',
				privacyInText: 'privacy information',
				mainTextMiddleTwo: ', and details about cookie usage and your options are available in ',
				cookiesInText: 'dedicated explanations',
				mainTextEnd: '.',
				acceptAll: 'Accept all',
				rejectAll: 'Reject all',
				customize: 'Customize',
				preferencesTitle: 'Cookie preferences',
				preferencesDescription: 'You can choose which optional cookie categories you allow. Strictly necessary cookies are always used because they are essential for site functionality, authentication, and security.',
				strictlyNecessaryTitle: 'Strictly necessary',
				alwaysActive: 'Always active',
				strictlyNecessaryDescription: 'Required for basic site operation, authentication, session continuity, security, and abuse prevention. These cookies cannot be disabled from this panel.',
				analyticsTitle: 'Analytics',
				analyticsDescription: 'They help us understand how the site is used, measure traffic, and improve performance and user experience.',
				marketingTitle: 'Marketing',
				marketingDescription: 'Used to measure campaign performance, display relevant communications, and integrate with promotional or advertising services, where available.',
				savePreferences: 'Save preferences'
			}
		}
    },
	watch: {
		showPreferences(value) {
			if (this.isMobile) {
				if (value) this.lockBackgroundScroll()
				else this.unlockBackgroundScroll()
			}
		}
	},
	mounted() {
		this.updateViewport()
		this.syncFromStorage()
		window.addEventListener('resize', this.updateViewport)
		window.addEventListener('open-cookie-preferences', this.handleOpenPreferences)
		window.openCookiePreferencesPanel = this.handleOpenPreferences
	},
	beforeUnmount() {
		this.unlockBackgroundScroll()
		window.removeEventListener('resize', this.updateViewport)
		window.removeEventListener('open-cookie-preferences', this.handleOpenPreferences)
		if (window.openCookiePreferencesPanel === this.handleOpenPreferences) {
			delete window.openCookiePreferencesPanel
		}
	},
    methods: {
		updateViewport() {
			this.isMobile = window.innerWidth < 768
			if (!this.isMobile) this.unlockBackgroundScroll()
		},
		lockBackgroundScroll() {
			if (document.body.dataset.cookieModalLock === '1') return
			this.lockScrollY = window.scrollY || window.pageYOffset || 0
			document.body.dataset.cookieModalLock = '1'
			document.documentElement.style.overflow = 'hidden'
			document.body.style.overflow = 'hidden'
			document.body.style.position = 'fixed'
			document.body.style.top = `-${this.lockScrollY}px`
			document.body.style.left = '0'
			document.body.style.right = '0'
			document.body.style.width = '100%'
		},
		unlockBackgroundScroll() {
			if (document.body.dataset.cookieModalLock !== '1') return
			delete document.body.dataset.cookieModalLock
			document.documentElement.style.overflow = ''
			document.body.style.overflow = ''
			document.body.style.position = ''
			document.body.style.top = ''
			document.body.style.left = ''
			document.body.style.right = ''
			document.body.style.width = ''
			window.scrollTo(0, this.lockScrollY || 0)
		},
		getStoredConsent() {
			try {
				const stored = window.localStorage.getItem(this.storageKey)
				if (!stored) return null
				const parsed = JSON.parse(stored)
				if (typeof parsed !== 'object' || parsed == null) return null
				return {
					necessary: true,
					analytics: !!parsed.analytics,
					marketing: !!parsed.marketing
				}
			} catch (e) {
				return null
			}
		},
		syncFromStorage() {
			const consent = this.getStoredConsent()
			if (!consent) {
				this.showBanner = true
				this.draft.analytics = false
				this.draft.marketing = false
				return
			}
			this.showBanner = false
			this.draft.analytics = consent.analytics
			this.draft.marketing = consent.marketing
		},
		applyConsent(consent) {
			const payload = {
				necessary: true,
				analytics: !!consent.analytics,
				marketing: !!consent.marketing,
				updatedAt: new Date().toISOString()
			}
			window.localStorage.setItem(this.storageKey, JSON.stringify(payload))
			localData.set('cookie_hide', true)
			this.showBanner = false
			this.showPreferences = false
			this.draft.analytics = payload.analytics
			this.draft.marketing = payload.marketing
			if (window.cookieConsent && typeof window.cookieConsent.apply === 'function') {
				window.cookieConsent.apply(payload)
			}
			window.dispatchEvent(new CustomEvent('cookie-consent-updated', { detail: payload }))
		},
		acceptAll() {
			this.applyConsent({ analytics: true, marketing: true })
		},
		rejectAll() {
			this.applyConsent({ analytics: false, marketing: false })
		},
		savePreferences() {
			this.applyConsent({ analytics: this.draft.analytics, marketing: this.draft.marketing })
		},
		openPreferences() {
			const consent = this.getStoredConsent()
			this.draft.analytics = consent ? consent.analytics : false
			this.draft.marketing = consent ? consent.marketing : false
			this.showPreferences = true
		},
		closePreferences() {
			this.showPreferences = false
		},
		handleOpenPreferences() {
			this.openPreferences()
		}
    },
};
</script>

<style scoped>
.cookie-banner-text {
	text-align: justify;
	text-justify: inter-word;
}

.cookie-banner-actions {
	margin-top: 18px !important;
}

@media (min-width: 768px) {
	.cookie-banner-content {
		display: grid;
		grid-template-columns: minmax(0, 1fr) 320px;
		column-gap: 24px;
		align-items: stretch;
	}

	.cookie-banner-text-wrap {
		align-self: center;
		padding-top: 8px;
		padding-bottom: 8px;
	}

	.cookie-banner-actions {
		margin-top: 0 !important;
		display: grid;
		grid-template-columns: auto auto;
		column-gap: 8px;
		row-gap: 8px;
		justify-content: center;
		align-content: center;
		align-items: center;
		justify-items: center;
		align-self: stretch;
		height: 100%;
	}

	.cookie-btn-customize {
		grid-column: 1 / -1;
		justify-self: center;
	}
}

@media (max-width: 767px) {
	.cookie-banner-shell {
		padding-left: max(18px, env(safe-area-inset-left));
		padding-right: max(18px, env(safe-area-inset-right));
	}

	.cookie-preferences-panel {
		padding-left: max(18px, env(safe-area-inset-left));
		padding-right: max(18px, env(safe-area-inset-right));
	}

	.cookie-banner-actions {
		display: grid;
		grid-template-columns: repeat(2, minmax(0, 1fr));
		width: 100%;
		gap: 6px;
	}

	.cookie-modal-actions {
		display: flex;
		flex-direction: row;
		flex-wrap: wrap;
		align-items: center;
		justify-content: center;
	}

	.cookie-banner-text {
		font-size: 14px;
		line-height: 1.45;
	}

	.cookie-modal-copy {
		text-align: justify;
		text-justify: inter-word;
		line-height: 1.52;
	}

	.cookie-banner-actions :deep(.btn) {
		width: 100%;
		justify-content: center;
		white-space: normal;
		min-width: 0;
		min-height: 40px;
		padding-left: 8px;
		padding-right: 8px;
	}

	.cookie-btn-customize {
		grid-column: 1 / -1;
	}

	.cookie-btn-customize :deep(.btn) {
		min-height: 44px;
	}

	.cookie-modal-actions :deep(.btn) {
		width: auto;
		justify-content: center;
		white-space: normal;
		flex: 1 1 calc(50% - 6px);
		min-width: 0;
	}
}
</style>
